import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import SuiInput from "components/SuiInput";

const MontoInput = ({
  value = "",
  onChange = () => {},
  onBlurExterno = () => {},
  autoFocus = false,
}) => {
  // Almacenamos el valor tanto en formato de visualización como en valor numérico
  const [displayValue, setDisplayValue] = useState("");
  const [numericValue, setNumericValue] = useState(0);
  const inputRef = useRef(null);
  const isEditingRef = useRef(false);

  // Función para formatear como moneda
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || isNaN(amount)) {
      return "";
    }

    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Función para extraer solo los dígitos
  const getNumericString = (str) => {
    if (!str) return "";
    return str.replace(/[^\d]/g, "");
  };

  // Sincronizamos con el valor externo
  useEffect(() => {
    // Sólo actualizamos si no estamos en modo edición para evitar conflictos
    if (!isEditingRef.current) {
      const parsedValue = parseFloat(value) || 0;
      setNumericValue(parsedValue);

      // Si tiene el foco, mostramos solo los dígitos
      if (document.activeElement === inputRef.current) {
        setDisplayValue(getNumericString(String(parsedValue)));
      } else {
        // Si no tiene el foco, mostramos el valor formateado
        setDisplayValue(formatCurrency(parsedValue));
      }
    }
  }, [value]);

  // Manejamos el cambio de valor en el input
  const handleChange = (e) => {
    const inputText = e.target.value;
    setDisplayValue(inputText);

    // Solo actualizamos el valor numérico si hay contenido válido
    const cleanNumeric = inputText.replace(/[^\d]/g, "");
    if (cleanNumeric) {
      const newNumericValue = parseInt(cleanNumeric, 10);
      setNumericValue(newNumericValue);
    }
  };

  // Al recibir el foco
  const handleFocus = () => {
    isEditingRef.current = true;

    // IMPORTANTE: Convertimos el valor a formato de edición (solo dígitos)
    // pero SIN CAMBIAR el valor numérico real
    setDisplayValue(getNumericString(String(numericValue)));
  };

  // Al perder el foco
  const handleBlur = () => {
    isEditingRef.current = false;

    // Si se ingresó algo válido
    if (displayValue && !isNaN(parseInt(displayValue.replace(/[^\d]/g, ""), 10))) {
      const finalNumericValue = parseInt(displayValue.replace(/[^\d]/g, ""), 10);
      setNumericValue(finalNumericValue);
      setDisplayValue(formatCurrency(finalNumericValue));
      onChange(finalNumericValue);
    } else {
      // Si no hay valor válido, volvemos al valor original formateado
      setDisplayValue(formatCurrency(numericValue));
    }

    onBlurExterno();
  };

  // Para manejar el autoFocus
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Cuando se active el autoFocus, enfocamos el input
      // pero esperamos un pequeño tiempo para asegurar que el valor ya esté establecido
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [autoFocus]);

  return (
    <SuiInput
      inputRef={inputRef}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder="$0"
      size="small"
      autoFocus={false} // Manejamos el autofocus manualmente
      inputProps={{
        style: { textAlign: "right", paddingRight: "8px" },
      }}
    />
  );
};

MontoInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onBlurExterno: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
};

export default MontoInput;
